import React, {useEffect, useState} from 'react'
import {useTranslation} from 'next-i18next'
import {useCollapse} from 'react-collapsed'
import {Box, Flex, HStack, IconButton, Text} from '@chakra-ui/react'
import styled from '@emotion/styled'
import isPropValid from '@emotion/is-prop-valid'
import Card from '_app/components/Card'
import PriceText from '_app/components/PriceText'
import {ChevronUpIcon} from '_app/components/Icons'
import {useCheckoutContext} from '../hooks'
import {CustomizableEngraveCartItem} from './PaidOptions'
import {JewelryItems} from './JewelryItems'
import LocalStorageHelper from '../../../helpers/LocalStorageHelper/LocalStorageHelper'

interface MobileBackdrop {
  show: boolean;
}

const MobileBackdrop = styled(Box, {
  shouldForwardProp: (prop: any) => isPropValid(prop),
})<MobileBackdrop>`
  position: relative;
  display: block;
  &:before {
    content: '';
    position: absolute;
    display: ${({ show }) => (show ? 'block' : 'none')};
    z-index: -1;
    top: 0;
    bottom: -1000%;
    left: 0;
    right: 0;
    background-color: rgb(0, 0, 0);
    background-color: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(2.7px);
  }
`

interface CheckoutSummaryProps {
  data: any;
}

const CheckoutSummary = ({ dataExtended }) => {
  const { t } = useTranslation('checkout')
  const { selectedShippingMethod, cartCheckoutData: data, refreshCartData }: any = useCheckoutContext()
  const {
    getCollapseProps: getCollapse,
    getToggleProps: getToggle,
    isExpanded: expanded,
  } = useCollapse()
  const [isExpanded, setExpanded] = useState(false)
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded })
  const jwItem = data?.cart?.items as CustomizableEngraveCartItem[]
  const prices = data?.cart?.prices
  const jwItemsCurrency = prices?.subtotal_including_tax?.currency ?? undefined
  const [startingPrice, setStartingPrice] = useState(prices?.subtotal_including_tax?.value ?? 0)
  const [cardPrice, setCardPrice] = useState(0)
  const discountValue =
      prices?.subtotal_including_tax?.value && prices?.subtotal_with_discount_excluding_tax?.value
      ? prices?.subtotal_including_tax?.value - prices?.subtotal_with_discount_excluding_tax?.value
      : 0
  useEffect(() => {
    if (data) {
      setCardPrice(LocalStorageHelper.get('cardPrice') || 0)
      setStartingPrice((prevVal: any) => prices?.subtotal_including_tax?.value ?? prevVal)
    }
  }, [data])
  useEffect(() => {
    if (dataExtended) {
      refreshCartData()
    }
  }, [dataExtended])
  return (
    <MobileBackdrop show={isExpanded}>
      <Card
        mb={{ base: 0, md: 6 }}
        position="relative"
        borderTop={{ base: 'none', md: '1px solid #ddd' }}
        boxShadow={isExpanded ? ' 0 5px 8px 0 rgba(83, 56, 24, 0.09)' : 'unset'}
      >
        <Box display={{ base: 'none', md: 'block' }}>
          <Box>
            <Flex
              justify="space-between"
              textAlign="left"
              pb={3}
              {...getToggle()}
            >
              <Text>{t('ORDER_SUMMARY')}</Text>
              <IconButton
                variant="icon"
                icon={<ChevronUpIcon />}
                aria-label={t('UpChevron')}
                transform={expanded ? 'rotate(0deg)' : 'rotate(180deg)'}
                transition="transform 0.2s"
              />
            </Flex>
            <Box {...getCollapse()}>
              <JewelryItems jewelry={jwItem} />
            </Box>
          </Box>
          <Flex
            justify="space-between"
            borderTop="solid 1px #ddd"
            pt={6}
            pb={4}
          >
            <Text fontWeight={300}>{t('PRODUCTS', { ns: 'common' })}</Text>
            {startingPrice && (
              <PriceText
                amount={{
                  currency: jwItemsCurrency || undefined,
                  value: startingPrice,
                }}
                floating
                minimumFractionDigits={2}
              />
            )}
          </Flex>
          {!!discountValue && data?.cart?.applied_coupons?.[0]?.code && (
            <Flex
              justify="space-between"
              borderTop="solid 1px #ddd"
              pt={6}
              pb={4}
            >
              <Text>{`${t('DISCOUNT_CODE')}: ${
                data?.cart?.applied_coupons?.[0]?.code
              }`}</Text>
              {startingPrice && (
                <PriceText
                  amount={{
                    currency: jwItemsCurrency || undefined,
                    value: -discountValue,
                  }}
                  floating
                  minimumFractionDigits={2}
                />
              )}
            </Flex>
          )}
          {
              data?.cart?.gift_card?.[0]?.value_amount ? (
                  <Flex
                      display={expanded ? 'none' : 'flex'}
                      justify="space-between"
                      pb={4}
                  >
                    <Text fontWeight={300}>
                      Karta podarunkowa
                      <br/>
                      Numer karty: {data?.cart?.gift_card?.[0]?.card_number || ''}
                    </Text>
                    <Text sx={{ color: 'green'}}>- {Number(data?.cart?.gift_card?.[0]?.value_amount).toFixed(2)} zł</Text>
                  </Flex>
              ) : ''
          }
          <Flex justify="space-between" borderBottom="solid 1px #ddd" pb={6}>
            <Text fontWeight={300}>{t('DELIVERY', { ns: 'common' })}</Text>
            <>
              {selectedShippingMethod?.amount?.value ? (
                <PriceText
                  amount={{
                    currency: jwItemsCurrency || undefined,
                    value: selectedShippingMethod?.amount?.value,
                  }}
                  floating
                  minimumFractionDigits={2}
                />
              ) : (
                <Text>---------</Text>
              )}
            </>
          </Flex>
          <Flex justify="space-between" py={6}>
            <Text fontWeight={300}>{t('TOTAL')}</Text>
            {startingPrice && (
              <PriceText
                  amount={{
                    currency: jwItemsCurrency || undefined,
                    value: prices?.grand_total?.value,
                  }}
                floating
                minimumFractionDigits={2}
              />
            )}
          </Flex>
        </Box>
        <Flex
          justify="space-between"
          textAlign="left"
          display={{ base: 'flex', md: 'none' }}
          {...getToggleProps({
            onClick: () => setExpanded(prevExpanded => !prevExpanded),
          })}
        >
          <Text>{t('TO_PAY')}</Text>
          {startingPrice && (
            <HStack>
              <PriceText
                amount={{
                  currency: jwItemsCurrency || undefined,
                  value:
                    (data?.cart?.prices?.subtotal_with_discount_excluding_tax
                      ?.value ?? startingPrice) +
                    (selectedShippingMethod?.amount?.value
                      ? selectedShippingMethod.amount.value
                      : 0),
                }}
                floating
                minimumFractionDigits={2}
              />
              <IconButton
                variant="icon"
                icon={<ChevronUpIcon />}
                aria-label={t('UpChevron')}
                transform={isExpanded ? 'rotate(0deg)' : 'rotate(-180deg)'}
                transition="transform 0.2s"
              />
            </HStack>
          )}
        </Flex>
        <Box
          display={{ base: 'block', md: 'none' }}
          pt={4}
          w="full"
          position="absolute"
          left={0}
          right={0}
          boxShadow=" 0 5px 8px 0 rgba(83, 56, 24, 0.09)"
        />
        <Box {...getCollapseProps()} mt={8}>
          <JewelryItems jewelry={jwItem} />
          <Flex
            display={expanded ? 'none' : 'flex'}
            justify="space-between"
            py={4}
            borderTop="solid 1px #ddd"
          >
            <Text fontWeight={300}>{t('PRODUCTS', { ns: 'common' })}</Text>
            {startingPrice && (
              <PriceText
                amount={{
                  currency: jwItemsCurrency || undefined,
                  value: startingPrice,
                }}
                floating
                minimumFractionDigits={2}
              />
            )}
          </Flex>
          {!!discountValue && !!data?.cart?.applied_coupons?.[0]?.code && (
            <Flex
              justify="space-between"
              borderTop="solid 1px #ddd"
              pt={6}
              pb={4}
            >
              <Text>{`${t('DISCOUNT_CODE')}: ${
                data?.cart?.applied_coupons?.[0]?.code
              }`}</Text>
              {startingPrice && (
                <PriceText
                  amount={{
                    currency: jwItemsCurrency || undefined,
                    value: -discountValue,
                  }}
                  floating
                  minimumFractionDigits={2}
                />
              )}
            </Flex>
          )}
          <Flex
            display={expanded ? 'none' : 'flex'}
            justify="space-between"
            borderBottom="solid 1px #ddd"
            pb={4}
          >
            <Text fontWeight={300}>{t('DELIVERY', { ns: 'common' })}</Text>
            <>
              {selectedShippingMethod?.amount?.value ? (
                <PriceText
                  amount={{
                    currency: jwItemsCurrency || undefined,
                    value: selectedShippingMethod?.amount?.value,
                  }}
                  floating
                  minimumFractionDigits={2}
                />
              ) : (
                <Text>---------</Text>
              )}
            </>
          </Flex>
          <Flex justify="space-between" pt={4} mb={4}>
            <Text fontWeight={300}>{t('TOTAL')}</Text>
            {startingPrice && (
              <PriceText
                amount={{
                  currency: jwItemsCurrency || undefined,
                  value: data?.cart?.prices?.grand_total,
                }}
                floating
                minimumFractionDigits={2}
              />
            )}
          </Flex>
        </Box>
      </Card>
    </MobileBackdrop>
  )
}

export default CheckoutSummary
